import React from "react";
import Head from "next/head";
import { useFela } from "react-fela";

import { useTheme } from "@/hooks/useTheme";
import { Grid } from "@/components/Grid";
import { Heading } from "@/components/Heading";
import { Link } from "@/components/Link";
import { IBlogPage } from "@/services/wordpress";
import { BlogPostCard } from "../BlogPostCard";

const SITE_URL = "https://magicul.io";

const pageHref = (page: number): string =>
  page <= 1 ? "/blog" : `/blog/page/${page}`;

export const BlogIndex: React.FC<{ blogPage: IBlogPage }> = ({ blogPage }) => {
  const { css } = useFela();
  const { colors, bp } = useTheme();
  const { posts, totalPages, currentPage } = blogPage;

  const title =
    currentPage <= 1
      ? "Blog - Magicul"
      : `Blog - Page ${currentPage} - Magicul`;
  const description =
    "Guides and news about design file conversion, Figma backups, and working across design tools like Figma, Sketch, XD, and Canva.";

  return (
    <>
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${SITE_URL}${pageHref(currentPage)}`} />
        {currentPage > 1 && (
          <link rel="prev" href={`${SITE_URL}${pageHref(currentPage - 1)}`} />
        )}
        {currentPage < totalPages && (
          <link rel="next" href={`${SITE_URL}${pageHref(currentPage + 1)}`} />
        )}
        <meta property="og:site_name" content="Magicul" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content={`${SITE_URL}${pageHref(currentPage)}`}
        />
      </Head>

      <Grid customStyle={{ flexDirection: "column", paddingBottom: 80 }}>
        <Heading
          as="h1"
          customStyle={{
            marginTop: 48,
            marginBottom: 40,
            textAlign: "left",
            fontSize: 40,
            color: colors.DARK_GREY,
          }}
        >
          {currentPage <= 1
            ? "Latest Posts"
            : `Latest Posts — Page ${currentPage}`}
        </Heading>

        <div
          className={css({
            display: "grid",
            gridTemplateColumns: "1fr",
            // Explicit units: the fela unit plugin treats columnGap as
            // unitless and would emit invalid "column-gap:32".
            columnGap: "32px",
            rowGap: "48px",
            [bp.FROM_TABLET]: { gridTemplateColumns: "1fr 1fr" },
            [bp.FROM_DESKTOP]: { gridTemplateColumns: "1fr 1fr 1fr" },
          })}
        >
          {posts.map(post => (
            <BlogPostCard key={post.id} post={post} />
          ))}
        </div>

        {totalPages > 1 && (
          <nav
            aria-label="Blog pagination"
            className={css({
              display: "flex",
              justifyContent: "center",
              gap: 12,
              marginTop: 56,
            })}
          >
            {Array.from({ length: totalPages }, (_, index) => index + 1).map(
              page => (
                <Link
                  key={page}
                  href={pageHref(page)}
                  customStyle={{
                    padding: "8px 14px",
                    borderRadius: 6,
                    textDecoration: "none",
                    fontWeight: page === currentPage ? 700 : 400,
                    color:
                      page === currentPage ? colors.WHITE : colors.CHARCOAL,
                    backgroundColor:
                      page === currentPage
                        ? colors.GRADIENT_PURPLE
                        : colors.CULTURED,
                  }}
                >
                  {String(page)}
                </Link>
              ),
            )}
          </nav>
        )}
      </Grid>
    </>
  );
};
